import { LitElement, html, css } from "lit";

/**
 * Sortable, searchable data table.
 *
 * Clicking a column header sorts by that field ascending; clicking again
 * reverses direction. The default sort is the first schema column ascending.
 *
 * On mobile the table collapses to a list: schema[0] becomes the row heading
 * (a link to entry.url), remaining columns appear as labeled sub-rows below.
 * Sort controls move to a sticky strip that remains visible while scrolling.
 *
 * When used inside `<library-browser>`, pass an already-filtered dataset and
 * set `searchFields` to an empty array — the host owns shared search state.
 *
 * @element data-table
 * @prop {Array}  entries      - Array of entry objects; each must include a `url` string.
 * @prop {Array}  schema       - Array of {key, label} column descriptors.
 * @prop {Array}  searchFields - Field names to include in search matching.
 */
export class DataTableElement extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    /* ── Search ── */
    .data-table__search {
      margin-bottom: var(--space-lg);
    }

    .data-table__search input {
      width: 100%;
      box-sizing: border-box;
    }

    /* ── Mobile sort strip (visible by default; hidden on wide screens) ── */
    .data-table__mobile-sort {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      padding: var(--space-sm) 0;
      position: sticky;
      top: 0;
      background: var(--color-surface);
      z-index: 1;
      border-bottom: 2px solid var(--color-teal);
      margin-bottom: var(--space-sm);
    }

    .data-table__mobile-sort select {
      flex: 1;
      font: inherit;
      padding: var(--space-xs) var(--space-sm);
      border: 1px solid var(--color-border);
      background: var(--color-surface);
      color: var(--color-text);
      outline: none;
    }

    .data-table__mobile-sort select:focus {
      border-color: var(--color-teal);
    }

    .data-table__mobile-sort button {
      background: var(--color-teal);
      border: 1px solid var(--color-teal);
      color: var(--color-surface);
      cursor: pointer;
      font: inherit;
      font-weight: 700;
      padding: var(--space-xs) var(--space-sm);
      min-width: 2.5rem;
    }

    /* ── Desktop table (hidden by default; shown on wide screens) ── */
    table {
      display: none;
      width: 100%;
      border-collapse: collapse;
    }

    th {
      text-align: left;
      padding: var(--space-sm);
      border-bottom: 2px solid var(--color-teal);
      cursor: pointer;
      user-select: none;
      white-space: nowrap;
      color: var(--color-teal);
      font-size: var(--step--1);
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    th:hover {
      color: var(--color-text);
    }

    .data-table__sort-indicator {
      margin-left: var(--space-xs);
    }

    td {
      text-align: left;
      padding: var(--space-sm);
      border-bottom: 1px solid var(--color-border);
    }

    tbody tr {
      cursor: pointer;
    }

    tbody tr:hover td {
      background: color-mix(in srgb, var(--color-teal) 8%, transparent);
    }

    /* ── Mobile list (visible by default; hidden on wide screens) ── */
    .data-table__mobile-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .data-table__mobile-item {
      border-bottom: 1px solid var(--color-border);
      padding: var(--space-sm) 0;
    }

    .data-table__row-heading {
      display: block;
      font-weight: bold;
      text-decoration: none;
      color: var(--color-teal);
      margin-bottom: var(--space-xs);
    }

    .data-table__row-heading:hover {
      text-decoration: underline;
    }

    .data-table__sub-row {
      display: flex;
      gap: var(--space-sm);
      font-size: var(--step--1);
    }

    .data-table__sub-label {
      opacity: 0.6;
    }

    /* ── Wide-screen layout ── */
    @media (min-width: 768px) {
      table {
        display: table;
      }

      .data-table__mobile-sort,
      .data-table__mobile-list {
        display: none;
      }
    }
  `;

  static properties = {
    entries:      { type: Array },
    schema:       { type: Array },
    searchFields: { type: Array, attribute: "search-fields" },
    _query:       { state: true },
    _sortField:   { state: true },
    _sortDir:     { state: true },
  };

  constructor() {
    super();
    this.entries      = [];
    this.schema       = [];
    this.searchFields = [];
    this._query       = "";
    this._sortField   = null;
    this._sortDir     = "asc";
  }

  get _activeSortField() {
    return this._sortField ?? this.schema[0]?.key ?? null;
  }

  get _filtered() {
    const q = this._query.trim().toLowerCase();
    if (!q) return this.entries;
    return this.entries.filter((entry) =>
      this.searchFields.some((field) => {
        const val = entry[field];
        if (Array.isArray(val))
          return val.some((v) => String(v).toLowerCase().includes(q));
        return String(val ?? "").toLowerCase().includes(q);
      }),
    );
  }

  get _sorted() {
    const field = this._activeSortField;
    if (!field) return this._filtered;
    return [...this._filtered].sort((a, b) => {
      const av = String(a[field] ?? "").toLowerCase();
      const bv = String(b[field] ?? "").toLowerCase();
      return this._sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }

  _handleHeaderClick(field) {
    if (this._activeSortField === field) {
      this._sortDir = this._sortDir === "asc" ? "desc" : "asc";
    } else {
      this._sortField = field;
      this._sortDir   = "asc";
    }
  }

  _handleMobileSortChange(e) {
    this._sortField = e.target.value;
    this._sortDir   = "asc";
  }

  _fieldValue(entry, key) {
    const val = entry[key];
    return Array.isArray(val) ? val.join(", ") : (val ?? "");
  }

  render() {
    const rows                  = this._sorted;
    const activeSortField       = this._activeSortField;
    const [heading, ...subCols] = this.schema;

    return html`
      ${this.searchFields.length > 0
        ? html`
            <div class="data-table__search">
              <input
                type="search"
                placeholder="Search…"
                .value="${this._query}"
                @input="${(e) => (this._query = e.target.value)}"
                aria-label="Search"
              />
            </div>
          `
        : null}

      <div class="data-table__mobile-sort" aria-label="Sort controls">
        <select
          @change="${this._handleMobileSortChange}"
          aria-label="Sort by"
        >
          ${this.schema.map(
            (col) => html`
              <option value="${col.key}" ?selected="${col.key === activeSortField}">
                ${col.label}
              </option>
            `,
          )}
        </select>
        <button
          @click="${() => (this._sortDir = this._sortDir === "asc" ? "desc" : "asc")}"
          aria-label="${this._sortDir === "asc" ? "Sort descending" : "Sort ascending"}"
        >
          ${this._sortDir === "asc" ? "↑" : "↓"}
        </button>
      </div>

      ${rows.length === 0
        ? html`<p class="data-table__empty">No results.</p>`
        : html`
            <table>
              <thead>
                <tr>
                  ${this.schema.map(
                    (col) => html`
                      <th
                        scope="col"
                        aria-sort="${activeSortField === col.key
                          ? (this._sortDir === "asc" ? "ascending" : "descending")
                          : "none"}"
                        @click="${() => this._handleHeaderClick(col.key)}"
                      >
                        ${col.label}
                        <span class="data-table__sort-indicator" aria-hidden="true">
                          ${activeSortField === col.key
                            ? (this._sortDir === "asc" ? "↑" : "↓")
                            : ""}
                        </span>
                      </th>
                    `,
                  )}
                </tr>
              </thead>
              <tbody>
                ${rows.map(
                  (entry) => html`
                    <tr @click="${() => (window.location = entry.url)}">
                      ${this.schema.map(
                        (col) => html`<td>${this._fieldValue(entry, col.key)}</td>`,
                      )}
                    </tr>
                  `,
                )}
              </tbody>
            </table>

            <ul class="data-table__mobile-list">
              ${rows.map(
                (entry) => html`
                  <li class="data-table__mobile-item">
                    <a href="${entry.url}" class="data-table__row-heading">
                      ${this._fieldValue(entry, heading?.key)}
                    </a>
                    ${subCols.map(
                      (col) => html`
                        <div class="data-table__sub-row">
                          <span class="data-table__sub-label">${col.label}</span>
                          <span>${this._fieldValue(entry, col.key)}</span>
                        </div>
                      `,
                    )}
                  </li>
                `,
              )}
            </ul>
          `}
    `;
  }
}

customElements.define("data-table", DataTableElement);
